<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold">Portal del Cliente</h1>
            <p class="text-subtitle-1 text-grey-darken-1">
              Bienvenido, {{ currentUser?.name || "Cliente" }}
            </p>
          </div>

          <v-btn color="error" variant="outlined" @click="handleLogout">
            <v-icon start>mdi-logout</v-icon>
            Cerrar Sesión
          </v-btn>
        </div>

        <v-card class="mb-4">
          <v-card-title class="bg-orange text-white">
            <v-icon start>mdi-folder-open</v-icon>
            Mis Documentos
          </v-card-title>

          <v-card-text>
            <v-alert
              v-if="filesError"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ filesError }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="files"
              class="elevation-1"
              :loading="loading"
              :group-by="groupBy"
              :row-props="fileRowProps"
              no-data-text="No hay documentos disponibles actualmente."
            >
              <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
                <tr>
                  <td :colspan="columns.length">
                    <div class="d-flex align-center justify-space-between w-100">
                      <div class="d-flex align-center">
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          @click="toggleGroup(item)"
                        >
                          <v-icon>
                            {{ isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                          </v-icon>
                        </v-btn>

                        <v-icon class="mr-2" color="amber-darken-2">mdi-folder</v-icon>
                        <strong>{{ item.value }}</strong>
                      </div>

                      <span class="text-medium-emphasis">
                        {{ countFiles(item.items) }} archivo(s)
                      </span>
                    </div>
                  </td>
                </tr>
              </template>

              <template #item.file_name="{ item }">
                <div class="d-flex align-center">
                  <v-icon class="mr-2">mdi-file-document</v-icon>
                  {{ item.file_name }}
                </div>
              </template>

              <!-- Acciones -->
              <template #item.actions="{ item }">
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    color="blue"
                    class="mr-2"
                    :href="resolveFileUrl(item)"
                    target="_blank"
                    title="Abrir documento"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    color="green"
                    :href="resolveFileUrl(item)"
                    :download="item.file_name"
                    title="Descargar archivo"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, ref, onMounted } from "vue"
import { useRouter } from 'vue-router'
import { useStore } from "vuex"

const router = useRouter()
const store = useStore()
const loading = ref(false)
const files = computed(() => store.getters["files/getFiles"])
const filesError = computed(() => store.state.files.error)
const currentUser = computed(() => store.getters.currentUser)

const API_BASE = process.env.VUE_APP_API_BASE;

const groupBy = ref([{ key: "grupo_carpeta", order: "asc" }])

// ✅ Encabezados visibles con títulos personalizados
const headers = [
  { title: "N°", value: "index", sortable: false, width: "60px" },
  { title: "Nombre del Archivo", value: "file_name" },
  { title: "Acciones", value: "actions", sortable: false, align: "center", width: "150px" },
]

const fetchFiles = async () => {
  loading.value = true
  const result = await store.dispatch("files/fetchFilesVigentes")
  loading.value = false

  if (result?.status === 401) {
    await store.dispatch("logout")
    await router.push("/login")
  }
}

const resolveFileUrl = (file) => {
  if (!file?.id_file) return ""

  const params = new URLSearchParams({
    action: "downloadFile",
    id: file.id_file,
  })

  return `${API_BASE}/ajax/files.php?${params.toString()}`
}

const getRawItem = (item) => item?.raw || item

const isFile = (item) => {
  const file = getRawItem(item)
  return file?.id_file !== null && file?.id_file !== undefined && file?.id_file !== ""
}

const countFiles = (items = []) => items.reduce((total, item) => {
  if (Array.isArray(item?.items)) return total + countFiles(item.items)
  return total + (isFile(item) ? 1 : 0)
}, 0)

const fileRowProps = ({ item }) => isFile(item)
  ? {}
  : { class: "empty-folder-placeholder" }

const handleLogout = async () => {
  await store.dispatch('logout')
  await router.push('/login')
}

onMounted(() => fetchFiles())
</script>

<style scoped>
.v-data-table .v-btn {
  transition: 0.2s ease;
}
.v-data-table .v-btn:hover {
  transform: scale(1.1);
}
.v-data-table th {
  font-weight: 600;
  background-color: #fafafa;
}
:deep(.empty-folder-placeholder) {
  display: none;
}
</style>
